import React, { useState, useEffect } from "react";
import { supabase } from "../../../lib/supabase";
import { Integration } from "../../../types/hotel";
import { Plug, Check, X, Power } from "lucide-react";
import { analytics } from "../../../lib/analytics";

interface IntegrationWithSaas extends Integration {
  saas_email: string;
  company_name: string;
}

export function Integrations() {
  const [integrations, setIntegrations] = useState<IntegrationWithSaas[]>([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [showPending, setShowPending] = useState(false);

  useEffect(() => {
    loadIntegrations();
  }, []);

  const loadIntegrations = async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        setError("User not authenticated");
        return;
      }

      const { data, error: queryError } = await supabase
        .from("integration_details")
        .select("*")
        .eq("hotel_id", user.id);

      if (queryError) throw queryError;

      setIntegrations(data || []);
    } catch (err) {
      setError("Failed to load integrations");
      console.error("Error loading integrations:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleIntegrationResponse = async (
    integrationId: string,
    accept: boolean
  ) => {
    try {
      setProcessingId(integrationId);
      setError("");

      // Get the integration details to access the saas_id
      const { data: integrationData, error: fetchError } = await supabase
        .from("integrations")
        .select("*")
        .eq("id", integrationId)
        .single();

      if (fetchError) throw fetchError;

      // Update the integration status
      const { error: updateError } = await supabase
        .from("integrations")
        .update({
          status: accept ? "active" : "rejected",
          updated_at: new Date().toISOString(),
        })
        .eq("id", integrationId);

      if (updateError) throw updateError;

      // Track hotel connection acceptance
      if (accept && integrationData) {
        analytics.trackHotelConnectionAccepted(
          integrationData.hotel_id,
          integrationData.saas_id
        );
      }

      // Create a notification for the SaaS provider
      if (integrationData && integrationData.saas_id) {
        const { data: hotelData } = await supabase
          .from("hotel_details")
          .select("hotel_name")
          .eq("id", integrationData.hotel_id)
          .single();

        const hotelName = hotelData?.hotel_name || "A hotel";

        const { error: notificationError } = await supabase
          .from("notifications")
          .insert({
            recipient_id: integrationData.saas_id,
            sender_id: integrationData.hotel_id,
            type: accept ? "integration_accepted" : "integration_rejected",
            title: accept
              ? "Integration Request Accepted"
              : "Integration Request Rejected",
            message: accept
              ? `${hotelName} has accepted your integration request`
              : `${hotelName} has rejected your integration request`,
            data: {
              integration_id: integrationId,
              hotel_id: integrationData.hotel_id,
              hotel_name: hotelName,
            },
          });

        if (notificationError) {
          console.error("Error creating notification:", notificationError);
        }
      }

      await loadIntegrations();

      // Show success message
      setError(`Integration ${accept ? "accepted" : "rejected"} successfully`);
      setTimeout(() => setError(""), 3000);
    } catch (err) {
      setError(`Failed to ${accept ? "accept" : "reject"} integration`);
      console.error("Error updating integration:", err);
    } finally {
      setProcessingId(null);
    }
  };

  const handleToggleStatus = async (
    integrationId: string,
    currentStatus: string
  ) => {
    try {
      setProcessingId(integrationId);
      setError("");

      const newStatus = currentStatus === "disabled" ? "active" : "disabled";

      // Get the integration details to access the saas_id
      const { data: integrationData, error: fetchError } = await supabase
        .from("integrations")
        .select("*")
        .eq("id", integrationId)
        .single();

      if (fetchError) throw fetchError;

      const { error: updateError } = await supabase
        .from("integrations")
        .update({
          status: newStatus,
          updated_at: new Date().toISOString(),
        })
        .eq("id", integrationId);

      if (updateError) throw updateError;

      // Create a notification for the SaaS provider
      if (integrationData && integrationData.saas_id) {
        const { data: hotelData } = await supabase
          .from("hotel_details")
          .select("hotel_name")
          .eq("id", integrationData.hotel_id)
          .single();

        const hotelName = hotelData?.hotel_name || "A hotel";

        const { error: notificationError } = await supabase
          .from("notifications")
          .insert({
            recipient_id: integrationData.saas_id,
            sender_id: integrationData.hotel_id,
            type:
              newStatus === "active"
                ? "integration_reactivated"
                : "integration_disabled",
            title:
              newStatus === "active"
                ? "Integration Reactivated"
                : "Integration Disabled",
            message:
              newStatus === "active"
                ? `${hotelName} has reactivated the integration`
                : `${hotelName} has disabled the integration`,
            data: {
              integration_id: integrationId,
              hotel_id: integrationData.hotel_id,
              hotel_name: hotelName,
            },
          });

        if (notificationError) {
          console.error("Error creating notification:", notificationError);
        }
      }

      await loadIntegrations();

      // Show success message
      setError(
        `Integration ${
          newStatus === "active" ? "reactivated" : "disabled"
        } successfully`
      );
      setTimeout(() => setError(""), 3000);
    } catch (err) {
      setError("Failed to update integration status");
      console.error("Error updating integration:", err);
    } finally {
      setProcessingId(null);
    }
  };

  if (loading) {
    return (
      <div className="extranet-container">
        <div className="extranet-container-loading">
          <div className="px-6 py-4 text-center text-sm text-gray-500">
            Loading integrations...
          </div>
        </div>
      </div>
    );
  }

  const pendingIntegrations = integrations.filter(
    (i) => i.status === "requested"
  );
  const activeIntegrations = integrations.filter((i) =>
    ["active", "disabled"].includes(i.status)
  );

  return (
    <div className="extranet-container">
      <div className="extranet-container-header">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Plug className="h-6 w-6 text-indigo-600" />
            <h2 className="text-xl font-bold text-gray-900">
              {showPending ? "Pending Requests" : "Active Integrations"}
            </h2>
          </div>
          <button
            onClick={() => setShowPending(!showPending)}
            className={`${showPending ? "btn-secondary" : "btn-primary"}`}
          >
            {showPending ? "View Active Integrations" : "View Requests"}
            {pendingIntegrations.length > 0 && !showPending && (
              <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100">
                {pendingIntegrations.length}
              </span>
            )}
          </button>
        </div>
      </div>

      <div className="extranet-container-body">
        {error && (
          <div
            className={`mb-4 p-4 ${
              error.includes("successfully") ? "bg-green-50" : "bg-red-50"
            } text-sm rounded-md border ${
              error.includes("successfully")
                ? "border-green-200"
                : "border-red-200"
            }`}
          >
            {error}
          </div>
        )}

        {showPending ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="table-header">Company</th>
                  <th className="table-header">Email</th>
                  <th className="table-header">Requested On</th>
                  <th className="table-header">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {pendingIntegrations.map((integration) => (
                  <tr key={integration.id} className="table-row">
                    <td className="table-cell">{integration.company_name}</td>
                    <td className="table-cell">{integration.saas_email}</td>
                    <td className="table-cell">
                      {new Date(integration.created_at).toLocaleDateString()}
                    </td>
                    <td className="table-cell space-x-2">
                      <button
                        onClick={() =>
                          handleIntegrationResponse(integration.id, true)
                        }
                        disabled={processingId === integration.id}
                        className="btn-primary inline-flex items-center mr-2"
                      >
                        <Check className="h-4 w-4 mr-1" />
                        Accept
                      </button>
                      <button
                        onClick={() =>
                          handleIntegrationResponse(integration.id, false)
                        }
                        disabled={processingId === integration.id}
                        className="btn-secondary inline-flex items-center text-red-600 hover:text-red-700"
                      >
                        <X className="h-4 w-4 mr-1" />
                        Reject
                      </button>
                    </td>
                  </tr>
                ))}
                {pendingIntegrations.length === 0 && (
                  <tr>
                    <td
                      colSpan={4}
                      className="px-6 py-4 text-center text-sm text-gray-500"
                    >
                      No pending integration requests
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="table-header">Company</th>
                  <th className="table-header">Email</th>
                  <th className="table-header">Status</th>
                  <th className="table-header">Connected Since</th>
                  <th className="table-header">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {activeIntegrations.map((integration) => (
                  <tr key={integration.id} className="table-row">
                    <td className="table-cell">{integration.company_name}</td>
                    <td className="table-cell">{integration.saas_email}</td>
                    <td className="table-cell">
                      <span
                        className={`status-badge ${
                          integration.status === "active"
                            ? "status-active"
                            : "status-inactive"
                        }`}
                      >
                        {integration.status === "active"
                          ? "Active"
                          : "Disabled"}
                      </span>
                    </td>
                    <td className="table-cell">
                      {new Date(integration.created_at).toLocaleDateString()}
                    </td>
                    <td className="table-cell">
                      <button
                        onClick={() =>
                          handleToggleStatus(integration.id, integration.status)
                        }
                        disabled={processingId === integration.id}
                        className={`inline-flex items-center ${
                          integration.status === "active"
                            ? "btn-secondary"
                            : "btn-primary"
                        } ${
                          processingId === integration.id
                            ? "opacity-50 cursor-not-allowed"
                            : ""
                        }`}
                      >
                        <Power className="h-4 w-4 mr-1" />
                        {integration.status === "active"
                          ? "Disable"
                          : "Reactivate"}
                      </button>
                    </td>
                  </tr>
                ))}
                {activeIntegrations.length === 0 && (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-6 py-4 text-center text-sm text-gray-500"
                    >
                      No active integrations
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
